import React, { useEffect } from 'react';
import {
  Row,
  Col,
  ButtonGroup,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { LAYOUT_MODE_TYPES } from 'Components/constants/layout';
import ActionButton from '../../common/ActionButton';
import { useProductForm } from 'features/products/context/ProductDetailContext';

interface DetailsNavigationProps {
  onNew?: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  onLoadImage?: () => void;
  onViewImage?: () => void;
  onScanBarcode?: () => void;
  onGenerateBarcode?: () => void;
  onConfigurator?: () => void;
  onTaskManager?: () => void;
  onSingleBundle?: () => void;
  onProcessProduct?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  showAuditProfile: boolean;
  setShowAuditProfile: React.Dispatch<React.SetStateAction<boolean>>;
}

const DetailsNavigation: React.FC<DetailsNavigationProps> = ({
  onNew,
  onCancel,
  onDelete,
  onCopy,
  onLoadImage,
  onViewImage,
  onScanBarcode,
  onGenerateBarcode,
  onConfigurator,
  onTaskManager,
  onSingleBundle,
  onProcessProduct,
  showAuditProfile,
  setShowAuditProfile,
}) => {
  
  // Use form context for submission and state tracking
  const { submitForm, resetForm, isDirty, isValid, isSubmitting } = useProductForm();
  
  // Get current theme from Redux
  const currentTheme = useSelector((state: RootState) => state.Layout.layoutModeType);

  // Debug form state changes
  useEffect(() => {
    console.log('Form State Changed:', { isDirty, isValid, isSubmitting });
  }, [isDirty, isValid, isSubmitting]);

  const handleSave = async () => {
    try {
      await submitForm()
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        resetForm();
        onCancel?.();
      }
    } else {
      onCancel?.();
    }
  };

  const actions = [
    {
      color: 'success',
      onClick: handleSave,
      icon: 'ri-save-line',
      label: isSubmitting ? 'Saving...' : 'Save',
      disabled: !isDirty || isSubmitting
    },
    {
      color: 'warning',
      onClick: handleCancel,
      icon: 'ri-close-line',
      label: 'Cancel',
      disabled: isSubmitting
    },
    {
      color: 'danger',
      onClick: onDelete,
      icon: 'ri-delete-bin-line',
      label: 'Delete',
      disabled: isSubmitting
    },
    {
      color: 'secondary',
      onClick: onCopy,
      icon: 'ri-file-copy-line',
      label: 'Copy',
      disabled: isSubmitting
    },
    {
      color: 'info',
      onClick: onLoadImage,
      icon: 'ri-image-add-line',
      label: 'Load Image',
      disabled: isSubmitting
    },
    {
      color: 'info',
      onClick: onViewImage,
      icon: 'ri-image-line',
      label: 'View Image',
      disabled: isSubmitting
    },
    {
      color: 'dark',
      onClick: onScanBarcode,
      icon: 'ri-barcode-line',
      label: 'Scan Barcode',
      disabled: isSubmitting
    },
    {
      color: 'dark',
      onClick: onGenerateBarcode,
      icon: 'ri-qr-code-line',
      label: 'Generate Barcode',
      disabled: isSubmitting
    },
    {
      color: 'primary',
      onClick: onConfigurator,
      icon: 'ri-settings-4-line',
      label: 'Configurator',
      disabled: isSubmitting
    },
    {
      color: 'success',
      onClick: onTaskManager,
      icon: 'ri-task-line',
      label: 'Task Manager',
      disabled: isSubmitting
    },
    {
      color: 'warning',
      onClick: onSingleBundle,
      icon: 'ri-box-3-line',
      label: 'Single Bundle',
      disabled: isSubmitting
    },
    {
      color: 'secondary',
      onClick: onProcessProduct,
      icon: 'ri-product-hunt-line',
      label: 'Process Product',
      disabled: isSubmitting
    },
  ] as const;

  return (
    <div className="mb-2">
      <Row>
        <Col>
          <div className="d-flex flex-wrap gap-2 ">
            {/* Form State Indicators */}
            <div className="me-3">
              {isDirty && (
                <span 
                  className="badge badge-warning me-2"
                  style={{
                    color: currentTheme === LAYOUT_MODE_TYPES.LIGHTMODE ? '#1f2937' : undefined,
                    fontWeight: '600'
                  }}
                >
                  <i className="ri-error-warning-line me-1"></i>
                  Unsaved Changes
                </span>
              )}
              {!isValid && (
                <span className="badge badge-danger me-2">
                  <i className="ri-error-warning-line me-1"></i>
                  Form has errors
                </span>
              )}
              {isSubmitting && (
                <span className="badge badge-info me-2">
                  <i className="ri-loader-4-line me-1"></i>
                  Saving...
                </span>
              )}
            </div>

            <ButtonGroup>
              <UncontrolledButtonDropdown>
                <button
                  className="py-0 btn btn-soft-primary btn-sm"
                  onClick={onNew}
                  disabled={isSubmitting}
                >
                  <i className="ri-add-line me-1"></i> New
                </button>
                <DropdownToggle
                  tag="button"
                  className="py-0 btn btn-soft-primary btn-sm"
                  size="sm"
                  split
                  disabled={isSubmitting}
                />
                <DropdownMenu>
                  <DropdownItem>New Product Kit</DropdownItem>
                  <DropdownItem>New Product</DropdownItem>
                  <DropdownItem>New Product Config</DropdownItem>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
            </ButtonGroup>

            {actions.map((action, index) => (
              <ActionButton
                key={index}
                color={action.color}
                onClick={action.onClick}
                icon={action.icon}
                label={action.label}
                disabled={action.disabled}
              />
            ))}
          </div>
        </Col>
        <Col xs="auto" className="ms-auto">
          <div>
            <button
              className="btn btn-sm btn-soft-info "
              aria-label={showAuditProfile ? 'Hide Audit Profile' : 'Show Audit Profile'}
              tabIndex={0}
              onClick={() => setShowAuditProfile((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setShowAuditProfile((prev) => !prev);
                }
              }}
              disabled={isSubmitting}
            >
              {showAuditProfile ? 'Hide Audit Profile' : 'Show Audit Profile'}
            </button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default DetailsNavigation;
